import * as z from "zod";
import { isoDate } from "./common";

/**
 * `partial`: the read was cut short, by the run's deadline or by a site that
 * refused everything for five minutes, or the source knows it missed listings
 * it cannot name; what was read is written, and nothing is delisted. `empty`:
 * the source yielded nothing, so nothing was
 * written. `failed`: the source could not be read, its index or every listing
 * it published, and `error` says why. `not-reached`: the run's deadline came
 * before this source's turn.
 */
export const EventImportSourceStatus = z.enum(["ok", "partial", "empty", "failed", "not-reached"]);
export type EventImportSourceStatus = z.output<typeof EventImportSourceStatus>;

export const EventImportSourceOutcome = z.object({
  sourceId: z.string(),
  status: EventImportSourceStatus,
  durationMs: z.number(),
  /** Listings the source published. */
  shows: z.number(),
  /** Of those, the ones that became events. */
  events: z.number(),
  inserted: z.number(),
  updated: z.number(),
  /** Upcoming listings the source stopped carrying. */
  delisted: z.number(),
  /** Listings that could not be read — usually no venue or an unrecognised date. */
  skipped: z.number(),
  error: z.string().nullable(),
});
export type EventImportSourceOutcome = z.output<typeof EventImportSourceOutcome>;

export const EventImportRun = z.object({
  id: z.string(),
  /** `"schedule"`, or `"manual(<userId>)"` for a back-office run. */
  trigger: z.string(),
  sourceIds: z.array(z.string()),
  startedAt: isoDate,
  finishedAt: isoDate.nullable(),
  /** Never finished and past its deadline: the process running it died. */
  interrupted: z.boolean(),
  deadlineReached: z.boolean(),
  /** The run itself broke, not a source. */
  error: z.string().nullable(),
  /** Imported upcoming shows the calendar lists once copies are grouped; null when the grouping did not run. */
  shows: z.number().nullable(),
  /** Copies of those shows hidden behind them. */
  hidden: z.number().nullable(),
  sources: z.array(EventImportSourceOutcome),
});
export type EventImportRun = z.output<typeof EventImportRun>;

export const EventImportStatus = z.object({
  /** Whether staff switched the nightly run on. Configuration problems keep it off regardless; a back-office run works either way. */
  scheduleEnabled: z.boolean(),
  cron: z.string(),
  timeZone: z.string(),
  configuredSourceIds: z.array(z.string()),
  availableSources: z.array(z.object({ id: z.string(), label: z.string() })),
  publish: z.boolean(),
  prune: z.boolean(),
  /** Configuration the import could not use; while there is any, the nightly run stays off. */
  configProblems: z.array(z.string()),
  state: z.enum(["idle", "running"]),
  currentRun: EventImportRun.nullable(),
  /** The last 20 runs, newest first. */
  history: z.array(EventImportRun),
});
export type EventImportStatus = z.output<typeof EventImportStatus>;

export const EventImportStarted = z.object({
  /** The run's id, as it appears in `history`. */
  runId: z.string(),
  /** The sources the run will import, in order. */
  sourceIds: z.array(z.string()),
});
export type EventImportStarted = z.output<typeof EventImportStarted>;
